import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { friendlyDeleteError } from '../utils/deleteError';
import { useDeleteConfirm } from '../hooks/useDeleteConfirm';
import { canDeleteForever } from '../utils/deleteGuard';
import { RecordIdLabel } from './ui/idColumn';
import { useNavigate } from 'react-router-dom';
import { MetricCard } from './MetricCards';
import EmptyState from './EmptyState';
import ConfirmDialog from './ui/ConfirmDialog';
import StatusBadge, { StatusBadgeVariant } from './ui/StatusBadge';
import { useStoredEvents } from '../hooks/useStoredEvents';
import {
  DecorItem,
  DecorCategory,
  DECOR_CATEGORY_ORDER,
  DECOR_CATEGORY_COLORS,
  DECOR_STATUS_LABELS,
  DecorStatus,
} from '../types/Decor';
import {
  loadDecorItems,
  updateDecorItem,
  archiveDecorItem,
  restoreDecorItem,
  deleteDecorItem,
  refreshDecorFromApi,
} from '../services/DecorService';
import { getProducts, refreshProductsFromApi } from '../services/ProductService';
import { Product } from '../types/Product';
import Icon from './Icon';
import { useNotifications } from './NotificationProvider';
import '../styles/components/DecorPlanner.css';
import { fmtId } from '../utils/jiraId';
import RecordListPublisher from './RecordListPublisher';

// StatusBadge variant per decor status. 'procured' previously had no CSS
// class at all (.dp-status-procured never existed) — items in that status
// rendered unstyled; now gets a real variant like the other three.
const DECOR_STATUS_VARIANT: Record<DecorStatus, StatusBadgeVariant> = {
  planned: 'warning',
  confirmed: 'info',
  procured: 'purple',
  installed: 'success',
};

// ── Colour swatch helper ──────────────────────────────────────────────────────

const GOLD_HEX = '#C9A84C';

function swatchColor(colorScheme: string): string | null {
  const match = colorScheme.match(/#[0-9a-fA-F]{3,6}/);
  if (match) return match[0];
  const lc = colorScheme.toLowerCase();
  if (lc.includes('gold')) return GOLD_HEX;
  if (lc.includes('ivory')) return '#FFFFF0';
  if (lc.includes('navy')) return '#1a2b5e';
  if (lc.includes('peach')) return '#FFCBA4';
  if (lc.includes('black')) return '#1a1a1a';
  if (lc.includes('neon')) return '#39ff14';
  return null;
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface DecorPlannerProps {
  categoryFilter?: string;
  statusFilter?: string;
  eventFilter?: string;
  onStatsChange?: (stats: { total: number; confirmed: number; installed: number }) => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

const DecorPlanner: React.FC<DecorPlannerProps> = ({
  categoryFilter = 'ALL',
  statusFilter = 'ALL',
  eventFilter,
  onStatsChange,
}) => {
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotifications();
  const [items, setItems] = useState<DecorItem[]>(() => loadDecorItems());
  const [openSections, setOpenSections] = useState<Set<DecorCategory>>(
    new Set(DECOR_CATEGORY_ORDER)
  );
  const projectEvents = useStoredEvents();
  const [archiveTarget, setArchiveTarget] = useState<string | null>(null);
  // 3-state (active/archived/all), matching Venues' filter — was a plain
  // Show/Hide toggle, which cannot express "everything regardless of
  // status" the way Venues' own filter already can.
  const [archiveFilter, setArchiveFilter] = useState<'active' | 'archived' | 'all'>('active');
  const [procuredItems, setProcuredItems] = useState<Product[]>(() =>
    getProducts().filter(p => p.types?.includes('decor'))
  );

  // Lives in Header's breadcrumb, alongside this page's other filters,
  // instead of a button floating in the page body below them. This page
  // still owns the real state; Header only mirrors it.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('decor-archived-sync', { detail: archiveFilter }));
  }, [archiveFilter]);

  useEffect(() => {
    const onSet = (e: Event) => setArchiveFilter((e as CustomEvent).detail);
    window.addEventListener('decor-archived-set', onSet);
    return () => window.removeEventListener('decor-archived-set', onSet);
  }, []);

  useEffect(() => {
    void refreshDecorFromApi().then(refreshed => setItems(refreshed));
  }, []);

  useEffect(() => {
    refreshProductsFromApi()
      .then(all => setProcuredItems(all.filter(p => p.types?.includes('decor'))))
      .catch(() => {});
  }, []);

  // ── Derived ──────────────────────────────────────────────────────────────

  const visibleItems = useMemo(() => {
    return items.filter(item => {
      if (archiveFilter === 'active' && item.archived) return false;
      if (archiveFilter === 'archived' && !item.archived) return false;
      if (categoryFilter !== 'ALL' && item.category !== categoryFilter) return false;
      if (statusFilter !== 'ALL' && item.status !== statusFilter) return false;
      if (eventFilter && !(item.event_ids ?? []).includes(eventFilter)) return false;
      return true;
    });
  }, [items, categoryFilter, statusFilter, eventFilter, archiveFilter]);


  const itemsByCategory = useMemo(() => {
    const map: Partial<Record<DecorCategory, DecorItem[]>> = {};
    for (const item of visibleItems) {
      if (!map[item.category]) map[item.category] = [];
      map[item.category]!.push(item);
    }
    return map;
  }, [visibleItems]);

  const stats = useMemo(() => ({
    total: items.length,
    confirmed: items.filter(i => i.status === 'confirmed').length,
    installed: items.filter(i => i.status === 'installed').length,
  }), [items]);

  useEffect(() => {
    onStatsChange?.(stats);
  }, [stats, onStatsChange]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const toggleSection = useCallback((cat: DecorCategory) => {
    setOpenSections(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  }, []);

  const openAdd = useCallback((defaultCategory?: DecorCategory) => {
    const qs = defaultCategory ? `?category=${encodeURIComponent(defaultCategory)}` : '';
    navigate(`/decor/new${qs}`);
  }, [navigate]);

  const openEdit = useCallback((item: DecorItem) => {
    navigate(`/decor/${fmtId('DC', item.id)}`);
  }, [navigate]);

  const handleArchive = useCallback((id: string) => {
    setArchiveTarget(id);
  }, []);

  const handleRestore = useCallback(async (id: string) => {
    try {
      await restoreDecorItem(id);
    } catch (err) {
      showError(friendlyDeleteError(err, { record: 'decor item', action: 'restore' }));
      return;
    }
    setItems(loadDecorItems());
  }, [showError]);

  // Delete forever is only offered on an archived item (the row action is
  // shown only then, and the service refuses otherwise).
  const forever = useDeleteConfirm<DecorItem>({
    record: 'decor item',
    perform: item => deleteDecorItem(item.id),
    onDone: () => { setItems(loadDecorItems()); showSuccess('Decor item deleted.'); },
  });
  const handleDelete = useCallback((item: DecorItem) => {
    if (canDeleteForever(item)) forever.request(item);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStatusCycle = useCallback(async (item: DecorItem) => {
    const cycle: DecorStatus[] = ['planned', 'confirmed', 'installed'];
    const next = cycle[(cycle.indexOf(item.status) + 1) % cycle.length];
    try {
      await updateDecorItem(item.id, { status: next });
    } catch (err) {
      // Don't re-read the cache — it still holds the old status, and showing
      // the new one would only survive until the next refresh.
      showError(err instanceof Error ? err.message : 'Failed to update status.');
      return;
    }
    setItems(loadDecorItems());
  }, [showError]);

  // ── Render ────────────────────────────────────────────────────────────────

  const visibleCategories = DECOR_CATEGORY_ORDER.filter(cat => (itemsByCategory[cat]?.length ?? 0) > 0);

  return (
    <div className="wt-container fade-in">
      <RecordListPublisher
        kind="decorElement"
        ids={visibleCategories.flatMap(cat => (itemsByCategory[cat] ?? []).map(item => item.id))}
      />
      <section className="stats-grid">
        <MetricCard label="Elements" value={stats.total} tone="blue" icon="list" />
        <MetricCard label="Confirmed" value={stats.confirmed} tone="green" icon="check" />
        <MetricCard label="Installed" value={stats.installed} tone="teal" icon="check" />
      </section>
      {visibleItems.length === 0 ? (
        <EmptyState title={archiveFilter === 'archived' ? 'No archived decor elements.' : 'No decor elements match the current filter.'} compact />
      ) : (
        <div className="dt-wrap">
          <div className="dt-scroll ds-scroll-x ds-scroll-x--framed">
            <table className="dt dt--hoverable">
              <thead>
                <tr>
                  <th className="dp-th-id">ID</th>
                  <th>Area</th>
                  <th>Element / Description</th>
                  <th>Color Scheme</th>
                  <th className="dp-th-qty">Qty</th>
                  <th>Vendor</th>
                  <th>Events</th>
                  <th className="dp-th-status">Status</th>
                  <th>Notes</th>
                  <th className="dp-th-actions"></th>
                </tr>
              </thead>

              {visibleCategories.map(cat => {
                const catItems = itemsByCategory[cat] ?? [];
                const isOpen = openSections.has(cat);
                const color = DECOR_CATEGORY_COLORS[cat];
                return (
                  <tbody key={cat}>
                    {/* Category group header row */}
                    <tr className="dp-group-row" onClick={() => toggleSection(cat)}>
                      <td colSpan={10}>
                        <div className="dp-group-row-inner">
                          <div className="dp-section-left">
                            <div className="dp-section-dot" style={{ /* ds-dynamic: category color from map */ background: color }} />
                            <span className="dp-section-label" style={{ /* ds-dynamic: category color from map */ color }}>{cat}</span>
                            <span className="dp-section-count">{catItems.length}</span>
                          </div>
                          <Icon name={isOpen ? 'chevronUp' : 'chevronDown'} size={14} />
                        </div>
                      </td>
                    </tr>

                    {/* Item rows — only when section open */}
                    {isOpen && catItems.map(item => (
                      <tr
                        key={item.id}
                        className="dt-row-clickable"
                        onClick={() => openEdit(item)}
                      >
                        <td>
                          <RecordIdLabel kind="decorElement" id={item.id} />
                        </td>
                        <td>
                          <span className="dp-area-text">{item.area}</span>
                          {item.venue_area && (
                            <span className="dp-venue-area-sub">{item.venue_area}</span>
                          )}
                        </td>
                        <td>
                          <span className="dp-element-cell">
                            {item.imageUrl && (
                              <img
                                src={item.imageUrl}
                                alt={item.decor_element || item.area}
                                className="dp-element-thumb"
                                onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                              />
                            )}
                            <span className="dp-element-text">{item.decor_element || '—'}</span>
                          </span>
                        </td>
                        <td>
                          {item.color_scheme ? (
                            <span className="dp-color-badge">
                              {swatchColor(item.color_scheme) && (
                                <span className="dp-color-swatch" style={{ /* ds-dynamic: swatch color from color scheme */ background: swatchColor(item.color_scheme)! }} />
                              )}
                              {item.color_scheme}
                            </span>
                          ) : (
                            <span className="dp-muted-dash">—</span>
                          )}
                        </td>
                        <td>
                          {item.quantity != null
                            ? <span className="dp-qty-text">{item.quantity}</span>
                            : <span className="dp-muted-dash">—</span>}
                        </td>
                        <td>
                          {item.vendor_name
                            ? <span className="dp-vendor-text">{item.vendor_name}</span>
                            : <span className="dp-vendor-empty">TBD</span>}
                        </td>
                        <td>
                          {(item.event_ids ?? []).length > 0 ? (
                            <div className="dp-event-pills">
                              {(item.event_ids ?? []).map(eid => {
                                const ev = projectEvents.find(e => e.id === eid);
                                return (
                                  <span key={eid} className="dp-event-pill">
                                    {ev?.title ?? eid}
                                  </span>
                                );
                              })}
                            </div>
                          ) : (
                            <span className="dp-muted-dash">—</span>
                          )}
                        </td>
                        <td onClick={e => e.stopPropagation()}>
                          <StatusBadge
                            variant={DECOR_STATUS_VARIANT[item.status]}
                            title="Click to advance status"
                            onClick={() => handleStatusCycle(item)}
                          >
                            {DECOR_STATUS_LABELS[item.status]}
                          </StatusBadge>
                        </td>
                        <td>
                          {item.notes
                            ? <span className="dp-notes-text" title={item.notes}>{item.notes}</span>
                            : null}
                        </td>
                        <td>
                          <div className="dp-row-actions">
                            {/* Edit icon removed — the row itself is already
                                clickable and opens the exact same edit page
                                (openEdit(item)), so this duplicated it. */}
                            {item.archived ? (
                              <>
                                <button
                                  className="dp-icon-btn"
                                  title="Restore"
                                  onClick={e => { e.stopPropagation(); handleRestore(item.id); }}
                                >
                                  <Icon name="refresh" size={14} />
                                </button>
                                <button
                                  className="dp-icon-btn delete"
                                  title="Delete forever"
                                  onClick={e => { e.stopPropagation(); handleDelete(item); }}
                                >
                                  <Icon name="trash" size={14} />
                                </button>
                              </>
                            ) : (
                              <button
                                className="dp-icon-btn delete"
                                title="Archive"
                                onClick={e => { e.stopPropagation(); handleArchive(item.id); }}
                              >
                                <Icon name="trash" size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}

                    {/* Add row — only when section open */}
                    {isOpen && (
                      <tr className="dp-add-item-row">
                        <td colSpan={8}>
                          <button
                            className="dp-add-inline-btn"
                            onClick={e => { e.stopPropagation(); openAdd(cat); }}
                          >
                            + Add {cat.toLowerCase()} element
                          </button>
                        </td>
                      </tr>
                    )}
                  </tbody>
                );
              })}
            </table>
          </div>
        </div>
      )}
      {procuredItems.length > 0 && (
        <div className="dp-procured-section">
          <h3 className="dp-procured-title">Procured Items</h3>
          <p className="dp-procured-hint">Items ordered through Procurement that will be used as décor</p>
          <div className="dp-procured-list">
            {procuredItems.map(p => {
              const supplier = p.suppliers?.find(s => s.isPreferred) ?? p.suppliers?.[0];
              return (
                <div key={p.id} className="dp-procured-row">
                  <div className="dp-procured-info">
                    <span className="dp-procured-name">{p.name}</span>
                    {supplier && <span className="dp-procured-supplier">via {supplier.name}</span>}
                  </div>
                  <div className="dp-procured-actions">
                    <span className="ds-badge ds-badge-success">Procured</span>
                    <button
                      className="pv-vendor-link dp-procured-view-link"
                      onClick={() => navigate('/procurement')}
                    >
                      View in Procurement
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <ConfirmDialog
        open={archiveTarget !== null}
        onClose={() => setArchiveTarget(null)}
        onConfirm={async () => {
          const id = archiveTarget;
          setArchiveTarget(null);
          if (!id) return;
          try {
            await archiveDecorItem(id);
          } catch (err) {
            showError(friendlyDeleteError(err, { record: 'decor item', action: 'archive' }));
            return;
          }
          setItems(loadDecorItems());
        }}
        title="Archive this decor item?"
        description="It will be hidden from the active list but not permanently deleted. You can restore it later from the Archived filter."
        confirmLabel="Archive"
      />
      <ConfirmDialog
        {...forever.dialogProps}
        title="Delete this decor item forever?"
        subject={forever.target?.decor_element}
        description="This cannot be undone."
        consequences={['The decor item and its notes']}
        confirmLabel="Delete forever"
      />
    </div>
  );
};

export default DecorPlanner;
